import { Bell, HelpCircle, Plus } from "lucide-react";
import { useLocation } from "react-router-dom";
import { WaveMark } from "@/components/ui/WaveMark";
import { useEntities } from "@/lib/hooks/useEntities";

const TITLES: Record<string, { title: string; sub?: string }> = {
  "/dashboard": { title: "Dashboard", sub: "Live operations" },
  "/approvals": { title: "Approvals", sub: "Awaiting your review" },
  "/workflows/new": { title: "New workflow", sub: "with Flow" },
  "/credentials": { title: "Credentials", sub: "Portal access" },
  "/reference-docs": { title: "Reference library", sub: "SOPs, CPT sheets, payer cheat-sheets" },
  "/workflows": { title: "Workflows", sub: "Training + lifecycle" },
  "/audit": { title: "Audit", sub: "Compliance queries" },
};

interface TopBarProps {
  onOpenChat?: () => void;
  onReplayTour?: () => void;
}

function BackendStatus() {
  const entities = useEntities();
  if (entities.isPending) {
    return (
      <span
        title="Checking backend…"
        className="flex items-center gap-1.5 rounded-pill border border-paper-line bg-paper-2 px-2 py-0.5 text-[11px] text-ink-3"
      >
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink-4" />
        Checking
      </span>
    );
  }
  if (entities.isError) {
    return (
      <span
        title={`Backend unreachable: ${entities.error instanceof Error ? entities.error.message : "unknown"}`}
        className="flex items-center gap-1.5 rounded-pill border border-status-failed/40 bg-status-failed-bg px-2 py-0.5 text-[11px] text-status-failed"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-status-failed" />
        Offline
      </span>
    );
  }
  const count = entities.data?.items.length ?? 0;
  return (
    <span
      title={`Connected to westus2 API · ${count} entities`}
      className="flex items-center gap-1.5 rounded-pill border border-status-success/30 bg-status-success-bg px-2 py-0.5 text-[11px] text-status-success"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-status-success" />
      Live
    </span>
  );
}

export function TopBar({ onOpenChat, onReplayTour }: TopBarProps = {}) {
  const { pathname } = useLocation();
  const meta =
    TITLES[pathname] ??
    (pathname.endsWith("/replay") && pathname.startsWith("/jobs/")
      ? { title: "Replay", sub: "Turn-by-turn agent audit" }
      : pathname.startsWith("/jobs/")
        ? { title: "Job", sub: pathname.split("/").pop() }
        : pathname.startsWith("/workflows")
          ? { title: "Workflows", sub: "Training + lifecycle" }
          : TITLES["/dashboard"]);

  return (
    <header className="flex h-[52px] shrink-0 items-center justify-between border-b border-paper-line bg-paper-0 px-4">
      <div className="flex items-baseline gap-2">
        <span className="text-md font-medium text-ink-1">{meta?.title}</span>
        {meta?.sub && (
          <>
            <span className="text-ink-5">·</span>
            <span className="text-sm text-ink-3">{meta.sub}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <BackendStatus />
        <button
          type="button"
          className="flex items-center gap-1 rounded-sm border border-teal-600 bg-teal-500 px-3 py-1.5 text-sm text-paper-3 transition-colors hover:bg-teal-600"
        >
          <Plus size={14} aria-hidden />
          New workflow
        </button>
        <button
          type="button"
          onClick={onOpenChat}
          aria-label="Open Flow chat"
          className="rounded-sm p-1.5 text-ink-2 transition-colors hover:bg-paper-tint"
          title="Ask Flow (⌘K)"
        >
          <WaveMark size={18} />
        </button>
        {onReplayTour && (
          <button
            type="button"
            onClick={onReplayTour}
            aria-label="Replay tour"
            title="Replay the tour"
            className="rounded-sm p-1.5 text-ink-2 transition-colors hover:bg-paper-tint"
          >
            <HelpCircle size={18} aria-hidden />
          </button>
        )}
        <button
          type="button"
          aria-label="Notifications"
          className="rounded-sm p-1.5 text-ink-2 transition-colors hover:bg-paper-tint"
        >
          <Bell size={18} aria-hidden />
        </button>
      </div>
    </header>
  );
}
